import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import {
  onChangeType,
  onTouchType,
} from 'src/app/core/models/common/custom.component.model';
import { Language } from 'src/app/core/models/languages.model';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
  VALIDATION_MESSAGE,
} from '../../../shared-constants';
import { DropdownPosition } from './form-select.constants';

export interface IOption {
  label: string | boolean;
  value: string | number | boolean | Language | IOption;
}

@Component({
  selector: 'app-form-select',
  templateUrl: './form-select.component.html',
  styleUrls: ['./form-select.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormSelectComponent),
      multi: true,
    },
  ],
})
export class FormSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  private readonly _injector = inject(Injector);

  @Input({ required: true }) options!: IOption[];
  @Input() selectedOption!: string;
  @Input() label = '';
  @Input() customClass = '';
  @Input() required = false;
  @Input() placeholder = '';
  @Input() isAppendToBody = false;
  @Input() isSearchable = true;
  @Input() isDisabled = false;
  @Input() hasAltHeight = false;
  @Input() hasSmallSelect = false;
  @Input() showBlank = false;
  @Input() dropdownPosition: DropdownPosition = DropdownPosition.AUTO;
  @Output() update = new EventEmitter();

  onChange: onChangeType = () => {};
  onTouch: onTouchType = () => {};
  hasIcons = false;
  control!: FormControl;
  private cvaDisabled = false;

  readonly CONSTANTS = { ...VALIDATION_MESSAGE };
  readonly IMAGES = IMAGE_SRC;
  readonly IMAGE_OPTIONS = STATUS_OPTIONS;

  ngOnInit(): void {
    this.hasIcons = this.options?.some(
      (value) =>
        value.label === STATUS_OPTIONS.ACTIVE ||
        value.value === STATUS_OPTIONS.ACTIVE ||
        value.label === STATUS_OPTIONS.DEACTIVATED ||
        value.value === STATUS_OPTIONS.DEACTIVATED,
    );

    const ngControl = this._injector.get(NgControl, null);
    if (ngControl) {
      this.control = ngControl.control as FormControl;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.options?.firstChange && changes.options?.currentValue) {
      this.options = [...changes.options.currentValue];
    }
  }

  registerOnChange(fn: onChangeType): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: onTouchType): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
  }

  writeValue(value: string) {
    this.selectedOption = value;
  }

  onModelChange(e: string) {
    this.selectedOption = e;
    this.onChange(e);
    this.update.emit(e);
  }

  customSearchFn(term: string, item: string | IOption) {
    if (typeof item === 'string') {
      return (
        item?.trim()?.toLowerCase()?.includes(term?.trim()?.toLowerCase()) ??
        true
      );
    } else {
      return (
        item?.label
          ?.toString()
          ?.trim()
          ?.toLowerCase()
          ?.includes(term?.trim()?.toLowerCase()) ?? true
      );
    }
  }

  get hasError(): boolean {
    return !!(
      this.control?.invalid &&
      (this.control?.dirty || this.control?.touched)
    );
  }

  get disabled(): boolean {
    return this.isDisabled || this.cvaDisabled;
  }
}
